import {
  COIMBATORE_ROUTE_MASTER,
  ROUTE_HUB,
  SERVICE_CITY_NAMES,
} from './coimbatoreRouteMaster';
import { getDestinationProfile } from './routeDestinationCopy';
import {
  CHENNAI_EXTRA_DESTINATIONS,
  CHENNAI_ROUTE_HUB,
  getChennaiRouteProfile,
} from './chennaiRouteCopy';
import {
  MADURAI_EXTRA_DESTINATIONS,
  MADURAI_ROUTE_HUB,
  getMaduraiRouteProfile,
} from './maduraiRouteCopy';
import {
  TRICHY_EXTRA_DESTINATIONS,
  TRICHY_ROUTE_HUB,
  getTrichyRouteProfile,
} from './trichyRouteCopy';
import {
  BANGALORE_EXTRA_DESTINATIONS,
  BANGALORE_PRIORITY_DESTINATIONS,
  BANGALORE_ROUTE_HUB,
  getBangaloreRouteProfile,
} from './bangaloreRouteCopy';
import {
  SALEM_EXTRA_DESTINATIONS,
  SALEM_PRIORITY_DESTINATIONS,
  SALEM_ROUTE_HUB,
  getSalemRouteProfile,
} from './salemRouteCopy';
import { DRIVER_BATA, MIN_ONEWAY_KM } from './bookingConfig';

export const slugifyPlace = (name) =>
  String(name)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export const toRoutePath = (origin, destination) =>
  `/${slugifyPlace(origin)}-to-${slugifyPlace(destination)}-taxi`;

export const displayPlaceName = (name) => {
  const value = String(name || '').trim();
  if (!value) return '';
  if (value === value.toLowerCase()) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
};

const isInCityList = (name) => SERVICE_CITY_NAMES.has(name);

const looksInterstatePair = (origin, destination) => (
  /Bangalore|Trivandrum|Tripati|Pondychery|Hosur/i.test(`${origin} ${destination}`)
);

const buildFaqs = (
  origin,
  destination,
  displayOrigin,
  displayDestination,
  direction,
  { distanceKm = null, travelHook = '' } = {},
) => {
  const routeLabel = `${displayOrigin} to ${displayDestination}`;
  const travelNote = travelHook
    ? ` ${travelHook.replace(/\.$/, '')}.`
    : '';

  const faqs = [
    {
      question: `How do I book a ${routeLabel} taxi on this page?`,
      answer: `Use the booking card on this ${routeLabel} landing page. Pickup defaults to ${origin} and drop to ${destination}. Enter your 10-digit mobile number, tap Get Fare Estimate, then choose Sedan, SUV or Crysta with the date and time you want to leave ${displayOrigin}. Confirming the review uses the same Easy Drop Taxi booking process as the homepage.`,
    },
    {
      question: direction === 'forward'
        ? `Is ${routeLabel} booked as one way by default?`
        : `Is this page for the return from ${displayOrigin} to ${displayDestination}?`,
      answer: direction === 'forward'
        ? `Yes. One Way is the default so you can finish in ${displayDestination} without holding a return vehicle.${travelNote} Switch to Round Trip in the same card if you need both legs, or open the ${displayDestination} to ${displayOrigin} Taxi page when you only need the return later.`
        : `Yes. This page is written for leaving ${displayOrigin} toward ${displayDestination}, not for repeating the outbound trip.${travelNote} Keep One Way selected unless the same cab must return, or use the ${displayDestination} to ${displayOrigin} Taxi page for the other direction.`,
    },
    {
      question: `Can I edit the ${displayOrigin} pickup or ${displayDestination} drop?`,
      answer: `Yes. Both fields stay editable on this ${routeLabel} form. Keep the city defaults if they match your trip, or type a more specific home, hotel, airport or mapped address before you estimate.`,
    },
    {
      question: `How is the ${routeLabel} fare calculated?`,
      answer: distanceKm
        ? `Easy Drop Taxi uses billed distance × published vehicle rate + ₹${DRIVER_BATA} driver bata. Distance comes from the live mapped drive for the pickup and drop you enter — not from treating the catalogue ${distanceKm} km figure as the fare distance. One-way bookings use a ${MIN_ONEWAY_KM} km minimum. Toll, state permit, parking, hill and waiting charges are extra when they apply, as shown at review.`
        : `Easy Drop Taxi uses billed distance × published vehicle rate + ₹${DRIVER_BATA} driver bata. Distance comes from the live mapped drive for the pickup and drop you enter. One-way bookings use a ${MIN_ONEWAY_KM} km minimum. Toll, state permit, parking, hill and waiting charges are extra when they apply, as shown at review.`,
    },
    {
      question: `Which vehicles can I choose for ${routeLabel}?`,
      answer: `Sedan, SUV and Crysta are available in the booking form for ${routeLabel}. After Get Fare Estimate, each vehicle card shows the total from the live distance, the published per-kilometre rate and driver bata.`,
    },
  ];

  if (looksInterstatePair(origin, destination)) {
    faqs.push({
      question: `Does the ${routeLabel} estimate include interstate extras?`,
      answer: `The on-screen total covers billed kilometres, the vehicle rate and driver bata. Toll, state permit, parking and waiting time stay extra when they apply on this interstate run, matching the note already shown when you review the booking.`,
    });
  } else if (distanceKm && distanceKm >= 350) {
    faqs.push({
      question: `Should I book ${routeLabel} in advance?`,
      answer: `For this longer outstation run, booking ahead helps the team arrange a vehicle for your travel date. After the estimate, enter a future pickup date and time for leaving ${displayOrigin}, then confirm the review so the request is received.`,
    });
  } else {
    faqs.push({
      question: `When do I set the pickup time from ${displayOrigin}?`,
      answer: `After Get Fare Estimate, the next step collects your vehicle, name, and the pickup date and time for leaving ${displayOrigin}. You can choose a future slot the same way as on other Easy Drop Taxi pages.`,
    });
  }

  return faqs;
};

const buildTravelParagraphs = (page, travelHook) => {
  const { displayOrigin, displayDestination, direction } = page;
  const second = direction === 'forward'
    ? `It works for a solo traveller, a couple or a family, depending on the vehicle. One-way booking means you do not have to arrange a second cab in ${displayDestination} unless you later want a round trip.`
    : `It works for a solo traveller, a couple or a family, depending on the vehicle. Because this page is the reverse journey, you book only the ${displayOrigin} to ${displayDestination} leg unless you switch the form to Round Trip.`;
  const third = `Distance and travel time on this page come from the same live mapping used to estimate the fare for ${displayOrigin} to ${displayDestination}. They are not taken from a catalogue kilometre, and the reverse direction is mapped on its own page. If mapping is unavailable, use Get Fare Estimate in the booking card.`;
  return [travelHook, second, third];
};

const buildPage = (origin, destination, distanceKm, direction, pairIndex) => {
  const profile = getDestinationProfile(destination === ROUTE_HUB ? origin : destination);
  if (!profile) {
    throw new Error(`Missing route copy for ${origin} → ${destination}`);
  }

  const displayOrigin = displayPlaceName(origin);
  const displayDestination = displayPlaceName(destination);
  const title = `${displayOrigin} to ${displayDestination} Taxi`;
  const slug = toRoutePath(origin, destination);
  const reverseSlug = toRoutePath(destination, origin);
  const isForward = direction === 'forward';

  return {
    slug,
    reverseSlug,
    origin,
    destination,
    displayOrigin,
    displayDestination,
    distanceKm,
    direction,
    pairIndex,
    hub: ROUTE_HUB,
    title,
    h1: title,
    seoTitle: `${title} | One Way Cab | Easy Drop Taxi`,
    seoDescription: isForward ? profile.leaveMeta : profile.returnMeta,
    heroLead: isForward ? profile.leaveHero : profile.returnHero,
    intro: isForward ? profile.leaveIntro : profile.returnIntro,
    travel: buildTravelParagraphs(
      { displayOrigin, displayDestination, direction },
      isForward ? profile.leaveTravel : profile.returnTravel,
    ),
    faqs: buildFaqs(origin, destination, displayOrigin, displayDestination, direction, {
      distanceKm,
      travelHook: isForward ? profile.leaveTravel : profile.returnTravel,
    }),
    originInCityList: isInCityList(origin),
    destinationInCityList: isInCityList(destination),
    ctaTitle: `Ready to Travel from ${displayOrigin} to ${displayDestination}?`,
    ctaBody: `Book your ${displayOrigin} to ${displayDestination} taxi with EasyDropTaxi.`,
  };
};

const DEDICATED_ROUTE_LINKS = [
  { path: '/chennai-to-madurai-taxi', label: 'Chennai to Madurai Taxi' },
  { path: '/madurai-to-chennai-taxi', label: 'Madurai to Chennai Taxi' },
];

const buildExtraHubPage = (origin, destination, direction, pairIndex, profile, hub) => {
  const displayOrigin = displayPlaceName(origin);
  const displayDestination = displayPlaceName(destination);
  const title = `${displayOrigin} to ${displayDestination} Taxi`;
  const isForward = direction === 'forward';

  return {
    slug: toRoutePath(origin, destination),
    reverseSlug: toRoutePath(destination, origin),
    origin,
    destination,
    displayOrigin,
    displayDestination,
    distanceKm: null,
    direction,
    pairIndex,
    hub,
    title,
    h1: title,
    seoTitle: `${title} | One Way Cab | Easy Drop Taxi`,
    seoDescription: isForward ? profile.leaveMeta : profile.returnMeta,
    heroLead: isForward ? profile.leaveHero : profile.returnHero,
    intro: isForward ? profile.leaveIntro : profile.returnIntro,
    travel: buildTravelParagraphs(
      { displayOrigin, displayDestination, direction },
      isForward ? profile.leaveTravel : profile.returnTravel,
    ),
    faqs: [
      ...buildFaqs(origin, destination, displayOrigin, displayDestination, direction, {
        travelHook: isForward ? profile.leaveTravel : profile.returnTravel,
      }),
      ...((isForward ? profile.leaveFaqs : profile.returnFaqs) || []),
    ],
    originInCityList: isInCityList(origin),
    destinationInCityList: isInCityList(destination),
    ctaTitle: `Ready to Travel from ${displayOrigin} to ${displayDestination}?`,
    ctaBody: `Book your ${displayOrigin} to ${displayDestination} taxi with EasyDropTaxi.`,
  };
};

const buildAllPages = () => {
  const pages = [];
  COIMBATORE_ROUTE_MASTER.forEach((item, pairIndex) => {
    pages.push(buildPage(ROUTE_HUB, item.destination, item.distanceKm, 'forward', pairIndex));
    pages.push(buildPage(item.destination, ROUTE_HUB, item.distanceKm, 'reverse', pairIndex));
  });

  const existingSlugs = new Set(pages.map((page) => page.slug));
  DEDICATED_ROUTE_LINKS.forEach((item) => existingSlugs.add(item.path));

  const appendExtras = (hub, destinations, getProfile, pairOffset) => {
    destinations.forEach((destination, index) => {
      const profile = getProfile(destination);
      if (!profile) {
        throw new Error(`Missing ${hub} route copy for ${destination}`);
      }
      const pairIndex = pairOffset + index;
      const extraPages = [
        buildExtraHubPage(hub, destination, 'forward', pairIndex, profile, hub),
        buildExtraHubPage(destination, hub, 'reverse', pairIndex, profile, hub),
      ];
      extraPages.forEach((page) => {
        if (!existingSlugs.has(page.slug)) {
          existingSlugs.add(page.slug);
          pages.push(page);
        }
      });
    });
  };

  appendExtras(CHENNAI_ROUTE_HUB, CHENNAI_EXTRA_DESTINATIONS, getChennaiRouteProfile, 1000);
  appendExtras(MADURAI_ROUTE_HUB, MADURAI_EXTRA_DESTINATIONS, getMaduraiRouteProfile, 2000);
  appendExtras(TRICHY_ROUTE_HUB, TRICHY_EXTRA_DESTINATIONS, getTrichyRouteProfile, 3000);
  appendExtras(BANGALORE_ROUTE_HUB, BANGALORE_EXTRA_DESTINATIONS, getBangaloreRouteProfile, 4000);
  appendExtras(SALEM_ROUTE_HUB, SALEM_EXTRA_DESTINATIONS, getSalemRouteProfile, 5000);

  return pages;
};

export const ROUTE_LANDING_PAGES = buildAllPages();

export const ROUTE_LANDING_BY_SLUG = ROUTE_LANDING_PAGES.reduce((acc, page) => {
  acc[page.slug] = page;
  return acc;
}, {});

export const getRouteLandingPage = (slug) => {
  if (!slug) return null;
  const key = slug.startsWith('/') ? slug : `/${slug}`;
  return ROUTE_LANDING_BY_SLUG[key] || null;
};

const PREFERRED_RELATED_PLACES = ['Chennai', 'Bangalore', 'Thanjavur'];

const ORIGIN_HUB_PATHS = {
  Coimbatore: '/coimbatore-taxi',
  Chennai: '/chennai-taxi',
  Madurai: '/madurai-taxi',
  Trichy: '/trichy-taxi',
  Bangalore: '/bangalore-taxi',
  Salem: '/salem-taxi',
};

export const getRelatedRouteLinks = (page) => {
  const links = [];
  const seen = new Set();

  const addLink = (path, label) => {
    if (!path || !label || seen.has(path) || path === page.slug) return;
    seen.add(path);
    links.push({ path, label });
  };

  const addOriginHubLink = () => {
    const hubPath = ORIGIN_HUB_PATHS[page.origin];
    if (hubPath) addLink(hubPath, `${page.origin} Taxi`);
  };

  const reverse = ROUTE_LANDING_BY_SLUG[page.reverseSlug];
  if (reverse) {
    addLink(reverse.slug, reverse.title);
  } else {
    const dedicatedReverse = DEDICATED_ROUTE_LINKS.find((item) => item.path === page.reverseSlug);
    if (dedicatedReverse) addLink(dedicatedReverse.path, dedicatedReverse.label);
  }

  if (page.hub === CHENNAI_ROUTE_HUB) {
    if (page.direction === 'forward') {
      addLink('/chennai-to-madurai-taxi', 'Chennai to Madurai Taxi');
      addLink('/chennai-to-coimbatore-taxi', 'Chennai to Coimbatore Taxi');
    } else {
      addLink('/madurai-to-chennai-taxi', 'Madurai to Chennai Taxi');
      addLink('/coimbatore-to-chennai-taxi', 'Coimbatore to Chennai Taxi');
    }

    ROUTE_LANDING_PAGES
      .filter((item) => (
        item.hub === CHENNAI_ROUTE_HUB
        && item.direction === page.direction
        && item.slug !== page.slug
      ))
      .forEach((item) => addLink(item.slug, item.title));

    addLink('/chennai-taxi', 'Chennai Taxi');
    addOriginHubLink();
    addLink('/', 'Home');
    addLink('/tariff', 'Tariff');
    return links;
  }

  if (page.hub === MADURAI_ROUTE_HUB) {
    if (page.direction === 'forward') {
      addLink('/madurai-to-chennai-taxi', 'Madurai to Chennai Taxi');
    } else {
      addLink('/chennai-to-madurai-taxi', 'Chennai to Madurai Taxi');
    }

    ROUTE_LANDING_PAGES
      .filter((item) => (
        item.hub === MADURAI_ROUTE_HUB
        && item.direction === page.direction
        && item.slug !== page.slug
      ))
      .forEach((item) => addLink(item.slug, item.title));

    addLink('/madurai-taxi', 'Madurai Taxi');
    addOriginHubLink();
    addLink('/', 'Home');
    addLink('/tariff', 'Tariff');
    return links;
  }

  if (page.hub === TRICHY_ROUTE_HUB) {
    ROUTE_LANDING_PAGES
      .filter((item) => (
        item.hub === TRICHY_ROUTE_HUB
        && item.direction === page.direction
        && item.slug !== page.slug
      ))
      .forEach((item) => addLink(item.slug, item.title));

    addLink('/trichy-taxi', 'Trichy Taxi');
    addOriginHubLink();
    addLink('/', 'Home');
    addLink('/tariff', 'Tariff');
    return links;
  }

  if (page.hub === BANGALORE_ROUTE_HUB) {
    BANGALORE_PRIORITY_DESTINATIONS.forEach((place) => {
      const origin = page.direction === 'forward' ? BANGALORE_ROUTE_HUB : place;
      const destination = page.direction === 'forward' ? place : BANGALORE_ROUTE_HUB;
      const related = ROUTE_LANDING_BY_SLUG[toRoutePath(origin, destination)];
      if (related) addLink(related.slug, related.title);
    });

    addLink('/bangalore-taxi', 'Bangalore Taxi');
    addOriginHubLink();
    addLink('/', 'Home');
    addLink('/tariff', 'Tariff');
    return links;
  }

  if (page.hub === SALEM_ROUTE_HUB) {
    SALEM_PRIORITY_DESTINATIONS.forEach((place) => {
      const origin = page.direction === 'forward' ? SALEM_ROUTE_HUB : place;
      const destination = page.direction === 'forward' ? place : SALEM_ROUTE_HUB;
      const related = ROUTE_LANDING_BY_SLUG[toRoutePath(origin, destination)];
      if (related) addLink(related.slug, related.title);
    });

    addLink('/salem-taxi', 'Salem Taxi');
    addOriginHubLink();
    addLink('/', 'Home');
    addLink('/tariff', 'Tariff');
    return links;
  }

  PREFERRED_RELATED_PLACES.forEach((place) => {
    const origin = page.direction === 'forward' ? page.hub : place;
    const destination = page.direction === 'forward' ? place : page.hub;
    const related = ROUTE_LANDING_BY_SLUG[toRoutePath(origin, destination)];
    if (related) {
      addLink(related.slug, related.title);
    }
  });

  addOriginHubLink();
  addLink('/', 'Home');
  addLink('/tariff', 'Tariff');
  return links;
};
